import api from "../../../utilidades/interceptor";

const urlApi = process.env.REACT_APP_API_KEY || 'http://localhost:3000/';

/**
 * Obtiene los datos de la cotización por ID
 * @param {string} cotizacionId - ID de la cotización
 * @param {string} turno - Turno E/S
 * @returns {Promise<Object>} - Datos de la cotización
 */
export const getCotizacionData = async (cotizacionId, turno) => {
    try {
        const res = await api.get(`${urlApi}/quotes/route/${cotizacionId}?turno=${turno}`);
        return res.data;
    } catch (error) {
        console.error("Error obteniendo datos de cotización:", error);
        throw error;
    }
};

/**
 * Obtiene los datos del trazo existente para edición
 * @param {string} trazoId - ID del trazo
 * @returns {Promise<Object>} - Datos del trazo
 */
export const getTrazoData = async (trazoId) => {
    try {
        const res = await api.get(`${urlApi}/quotes/trazos/${trazoId}/edit`);
        return res.data;
    } catch (error) {
        console.error("Error obteniendo datos del trazo:", error);
        throw error;
    }
};

/**
 * Guarda una nueva ruta/trazo
 * @param {Object} data - Datos del trazo
 * @returns {Promise<Object>} - Respuesta del servidor
 */
export const addRoute = async (data) => {
    try {
        const res = await api.post(`${urlApi}/quotes/trazos`, data);
        return res.data;
    } catch (error) {
        console.error("Error guardando ruta:", error);
        throw error;
    }
};

/**
 * Actualiza una ruta/trazo existente
 * @param {Object} data - Datos del trazo
 * @returns {Promise<Object>} - Respuesta del servidor
 */
export const updateRoute = async (data) => {
    try {
        const res = await api.put(`${urlApi}/quotes/trazos/edit`, data);
        return res.data;
    } catch (error) {
        console.error("Error actualizando ruta:", error);
        throw error;
    }
};

/**
 * Valida si todos los turnos tienen horarios configurados para las paradas
 * @param {Array} markerDetails - Detalles de las paradas
 * @param {Array} estructuras - Estructuras/turnos de la cotización
 * @returns {Object} - Resultado de validación
 */
export const validateHorariosPorTurno = (markerDetails, estructuras) => {
    const errors = [];

    markerDetails.forEach((parada, paradaIndex) => {
        if (!parada.horariosPorTurno || parada.horariosPorTurno.length === 0) {
            errors.push(`La parada "${parada.name}" no tiene horarios configurados`);
            return;
        }

        estructuras.forEach(estructura => {
            const horarioTurno = parada.horariosPorTurno.find(h => h.estructuraId === estructura.id);
            if (!horarioTurno || !horarioTurno.horario) {
                const tipo = estructura.nombreTipo ? ` ${estructura.nombreTipo}` : '';
                errors.push(`La parada "${parada.name}" no tiene horario para el turno ${estructura.nombreTurno}${tipo}`);
            }
        });
    });

    return {
        isValid: errors.length === 0,
        errors
    };
};

/**
 * Formatea los datos para enviar al backend
 * @param {Object} routeData - Datos de la ruta
 * @param {string} cotizacionId - ID de la cotización
 * @param {string} tipoLabel - Tipo de ruta (entrada/salida)
 * @param {string} cliente - ID del cliente
 * @returns {Object} - Datos formateados
 */
export const formatRouteDataForApi = (routeData, cotizacionId, tipoLabel, cliente) => {
    return {
        cotizacionId,
        tipoLabel,
        clienteId: cliente,
        usuarioId: routeData.usuarioId,
        nombreRuta: routeData.routeName,
        color: routeData.assignedColor,
        polyline: routeData.polyline,
        paradas: routeData.markerDetails.map((parada, index) => ({
            orden: index,
            nombre: parada.paradaControl ? 'Parada de control' : parada.name,
            latitud: parada.lat,
            longitud: parada.lng,
            referencia: parada.reference || '',
            paradaControl: !!parada.paradaControl,
            horariosPorTurno: parada.horariosPorTurno || []
        })),
        resumen: routeData.summary
    };
};

/**
 * Obtiene el polyline de la ruta de IDA como referencia
 * para pre-cargar la ruta de regreso de forma invertida
 * @param {string} cotizacionId
 * @returns {Promise<{ success: boolean, data: { polyline: string|null } }>}
 */
export const getIdaRouteReference = async (cotizacionId) => {
    try {
        const res = await api.get(`${urlApi}/quotes/route/${cotizacionId}/ida-reference`);
        return res.data;
    } catch (error) {
        console.error('Error obteniendo referencia de ruta de ida:', error);
        throw error;
    }
};

/**
 * Decodifica el polyline en base64 a un array de coordenadas
 * @param {string} base64String
 * @returns {Array<{lat: number, lng: number}>}
 */
export const decodePolylineBase64 = (base64String) => {
    try {
        const jsonString = atob(base64String);
        const parsed = JSON.parse(jsonString);
        return parsed?.geometry?.coordinates ?? [];
    } catch (error) {
        console.error('Error decodificando polyline:', error);
        return [];
    }
};


/**
 * Parsea coordenadas GeoJSON [lng, lat] a formato Leaflet [lat, lng]
 * @param {string|object} polyline - GeoJSON crudo
 * @returns {Array<[number, number]>}
 */
export const parsePolylineGeoJSON = (polyline) => {
    if (!polyline) return [];
    try {
        const str = typeof polyline === 'string' ? polyline : JSON.stringify(polyline);
        const parsed = JSON.parse(str);
        const coords = parsed?.geometry?.coordinates ?? [];
        console.log('todas', coords
            .filter(c => Array.isArray(c) && c.length >= 2)
            .map(([lng, lat]) => [lat, lng]))
        return coords
            .filter(c => Array.isArray(c) && c.length >= 2)
            .map(([lng, lat]) => [lat, lng]);
    } catch (error) {
        console.error('Error parseando polyline GeoJSON:', error);
        return [];
    }
};

/**
 * Obtiene un trazo con los horarios SOLO de su propia estructura.
 * Usar cuando se edita en modo individual (sin afectar otros turnos).
 *
 * Backend: llama a getTrazoById() → no cruza con otros trazos del tipo
 *
 * @param {string|number} trazoId
 * @returns {Promise<Object>}
 */
export const getTrazoIndividual = async (trazoId) => {
    try {
        const res = await api.get(`${urlApi}/quotes/trazos/${trazoId}`);
        return res.data;
    } catch (error) {
        console.error('Error obteniendo trazo individual:', error);
        throw error;
    }
};

/**
 * Actualiza SOLO el trazo indicado sin tocar los demás (modo individual).
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export const updateRouteIndividual = async (data) => {
    try {
        const res = await api.put(`${urlApi}/quotes/trazos/${data.id}`, data);
        return res.data;
    } catch (error) {
        console.error('Error actualizando trazo individual:', error);
        throw error;
    }
};
